import { ErrorMessage } from "@/components/ErrorMessage";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import { Pill } from "@/components/podkit/status/Pill";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { RunnerDetailsSection, type SectionStatus } from "@/components/runners/details/RunnerDetailsSection";
import { useMembership } from "@/hooks/use-membership";
import { useListGroups } from "@/queries/group-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useListRunnerPolicies, useUpdateSharingSetting } from "@/queries/runner-queries";
import { formatError } from "@/utils/errors";
import type { PlainMessage } from "@bufbuild/protobuf";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";

import type { Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

const GroupnNames = {
    OrgMembers: "org-members",
};

const SharingSetting = {
    OnlyMe: "only-me",
    AnyoneInOrg: "anyone-in-org",
    SpecificPeople: "specific-people",
};

export const RunnerSharing: FC<{ runner: PlainMessage<Runner>; hasSignedUp: boolean }> = ({ runner, hasSignedUp }) => {
    const runnerId = runner.runnerId;
    const [sharing, setSharing] = useState<string>(SharingSetting.OnlyMe);

    const { toast } = useToast();
    const { data: policies, isLoading: isLoadingPolicies, error: policiesError } = useListRunnerPolicies(runnerId);
    const { data: organization, isLoading: isLoadingOrganization, error: organizationError } = useOrganization();
    const { data: groups, isLoading: isLoadingGroups, error: groupsError } = useListGroups();
    const { membership, isPending: isLoadingMembership } = useMembership();

    const hasError: boolean = !!policiesError || !!organizationError || !!groupsError;
    const isLoading = isLoadingPolicies || isLoadingOrganization || isLoadingGroups || isLoadingMembership;

    const orgMembersGroup = useMemo(() => {
        if (!organization || !groups) {
            return;
        }
        return groups.groups.find((g) => g.organizationId === organization.id && g.name === GroupnNames.OrgMembers);
    }, [organization, groups]);

    const updateSharingSetting = useUpdateSharingSetting(runnerId);

    const isManagedRunner = runner.provider === RunnerProvider.MANAGED;
    let status: SectionStatus = "loading";
    if (!isLoading) {
        if (!hasSignedUp) {
            status = "locked";
        } else {
            status = "optional";
        }
    }

    // TODO: Enable runner:grant permission check once backend supports it
    // Currently RUNNER_ADMIN role doesn't have sufficient permissions for sharing actions
    // const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    // const { hasPermission } = useResourcePermissions({ enabled: isGroupManagementEnabled });
    // const canGrantRunner = isGroupManagementEnabled
    //     ? hasPermission(ResourceType.RUNNER, runnerId, "runner:grant")
    //     : isAdmin;
    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const canGrantRunner = isAdmin;
    const readOnly = status === "locked" || !canGrantRunner;

    const onSharingSettingChange = useCallback(
        async (value: string) => {
            if (!orgMembersGroup) {
                return;
            }
            if (value === "only-me" || value === "anyone-in-org") {
                setSharing(value);
                try {
                    await updateSharingSetting.mutateAsync({ setting: value, groupId: orgMembersGroup?.id });
                    toast({
                        title: "Sharing updated",
                        description:
                            value == "only-me"
                                ? "Runner is now only available to you."
                                : "Runner is now available to anyone in your organization.",
                    });
                } catch (error) {
                    setSharing(sharing);
                    toast({
                        title: "Failed to update sharing setting",
                        description: formatError(error),
                    });
                }
            }
        },
        [orgMembersGroup, sharing, toast, setSharing, updateSharingSetting],
    );

    useEffect(() => {
        if (policies && orgMembersGroup) {
            const sharedWithOrg = policies.policies.find((p) => p.groupId === orgMembersGroup?.id);
            setSharing(sharedWithOrg ? SharingSetting.AnyoneInOrg : SharingSetting.OnlyMe);
        }
    }, [policies, setSharing, orgMembersGroup]);

    return (
        <RunnerDetailsSection status={status} title="Sharing" hideStatus={isManagedRunner}>
            <div className="flex flex-col gap-4" data-testid="sharing-section">
                <div className="flex flex-col gap-1">
                    <SkeletonText ready={status !== "loading"} size="lg" className="w-48">
                        <Text className={cn(status === "locked" && "text-content-secondary", "text-base")}>
                            Specify who can use this runner
                        </Text>
                    </SkeletonText>
                </div>
                <SkeletonBlock ready={!isLoading} failed={hasError} className="mt-1 h-[100px]">
                    {!hasError && (
                        <RadioGroup value={sharing} onValueChange={onSharingSettingChange}>
                            <div className="flex items-center justify-start gap-2">
                                <RadioGroupItem
                                    className="h-4 w-4"
                                    value={SharingSetting.OnlyMe}
                                    id={SharingSetting.OnlyMe}
                                    disabled={readOnly}
                                />
                                <label htmlFor={SharingSetting.OnlyMe} className="cursor-pointer">
                                    <Text className="text-base">Only organization admins</Text>
                                </label>
                            </div>

                            <div className="flex items-center justify-start gap-2">
                                <RadioGroupItem
                                    className="h-4 w-4"
                                    value={SharingSetting.AnyoneInOrg}
                                    id={SharingSetting.AnyoneInOrg}
                                    disabled={readOnly}
                                />
                                <label htmlFor={SharingSetting.AnyoneInOrg} className="cursor-pointer">
                                    <Text className="text-base">Anyone in {organization?.name}</Text>
                                </label>
                            </div>

                            <div className="flex items-center justify-start gap-2">
                                <RadioGroupItem
                                    className="h-4 w-4"
                                    value={SharingSetting.SpecificPeople}
                                    id={SharingSetting.SpecificPeople}
                                    disabled={true}
                                />
                                <label
                                    htmlFor={SharingSetting.SpecificPeople}
                                    className="flex cursor-pointer items-center justify-center gap-2"
                                >
                                    <Text className="text-base">Specific people</Text>
                                    <Pill variant="neutral" size="sm">
                                        Coming soon
                                    </Pill>
                                </label>
                            </div>
                        </RadioGroup>
                    )}
                </SkeletonBlock>
                <ErrorMessage error={policiesError} />
                <ErrorMessage error={organizationError} />
                <ErrorMessage error={groupsError} />
            </div>
        </RunnerDetailsSection>
    );
};
